/**
 * LLM call logger — stores prompt/response telemetry in IndexedDB
 * for post-hoc debugging of the Gibbs text-to-action pipeline.
 *
 * All operations are fire-and-forget: logging failures never block
 * or crash the pipeline.
 *
 * @module gibbs/telemetry/llmLogger
 */

const DB_NAME = 'gibbs_telemetry';
const STORE_NAME = 'llm_calls';
const DB_VERSION = 1;
const MAX_ENTRIES = 500;

/**
 * Open (or create) the IndexedDB database.
 * @returns {Promise<IDBDatabase>}
 */
function openDB() {
    return new Promise((resolve, reject) => {
        try {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                    store.createIndex('timestamp', 'timestamp', { unique: false });
                }
            };

            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror = () => reject(request.error);
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Auto-increment counter for entry IDs within a session.
 * Combined with timestamp for a globally unique key.
 */
let seqCounter = 0;

/**
 * Log an LLM call to IndexedDB.
 *
 * @param {Object} entry
 * @param {string} entry.userMessage
 * @param {string} entry.tier
 * @param {number} entry.systemPromptLength
 * @param {number} entry.messagesCount
 * @param {string} entry.model
 * @param {string} entry.responseContent
 * @param {string} entry.resultType
 * @param {number} entry.retryAttempt
 * @param {string[]} entry.parseErrors
 * @param {string[]} entry.validationErrors
 * @param {number} entry.duration_ms
 * @returns {Promise<void>}
 */
export async function logLLMCall(entry) {
    try {
        const db = await openDB();
        const now = Date.now();
        seqCounter += 1;

        const record = {
            id: `${now}-${seqCounter}`,
            timestamp: now,
            userMessage: entry.userMessage || '',
            tier: entry.tier || '',
            systemPromptLength: entry.systemPromptLength || 0,
            messagesCount: entry.messagesCount || 0,
            model: entry.model || '',
            responseContent: entry.responseContent || '',
            resultType: entry.resultType || '',
            retryAttempt: entry.retryAttempt || 0,
            parseErrors: entry.parseErrors || [],
            validationErrors: entry.validationErrors || [],
            duration_ms: entry.duration_ms || 0,
        };

        await new Promise((resolve, reject) => {
            try {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                tx.objectStore(STORE_NAME).put(record);
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            } catch (err) {
                reject(err);
            }
        });

        // Auto-prune: keep only the most recent MAX_ENTRIES
        await pruneOldEntries(db);
    } catch (_) {
        // Fire-and-forget — never throw from the logger
    }
}

/**
 * Remove oldest entries beyond MAX_ENTRIES.
 * @param {IDBDatabase} db
 * @returns {Promise<void>}
 */
async function pruneOldEntries(db) {
    try {
        const count = await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const req = tx.objectStore(STORE_NAME).count();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });

        if (count <= MAX_ENTRIES) return;

        const toDelete = count - MAX_ENTRIES;

        await new Promise((resolve, reject) => {
            try {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                const index = store.index('timestamp');
                const cursor = index.openCursor(); // ascending = oldest first
                let deleted = 0;

                cursor.onsuccess = (event) => {
                    const c = event.target.result;
                    if (c && deleted < toDelete) {
                        c.delete();
                        deleted += 1;
                        c.continue();
                    }
                };

                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            } catch (err) {
                reject(err);
            }
        });
    } catch (_) {
        // Non-fatal
    }
}

/**
 * Retrieve recent LLM log entries, newest first.
 *
 * @param {number} [limit=10] - Maximum entries to return
 * @returns {Promise<Object[]>}
 */
export async function getLLMLog(limit = 10) {
    try {
        const db = await openDB();

        return await new Promise((resolve, reject) => {
            try {
                const tx = db.transaction(STORE_NAME, 'readonly');
                const index = tx.objectStore(STORE_NAME).index('timestamp');
                const request = index.openCursor(null, 'prev'); // newest first
                const results = [];

                request.onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (cursor && results.length < limit) {
                        results.push(cursor.value);
                        cursor.continue();
                    } else {
                        resolve(results);
                    }
                };

                request.onerror = () => reject(request.error);
            } catch (err) {
                reject(err);
            }
        });
    } catch (_) {
        return [];
    }
}

/**
 * Clear all LLM log entries.
 * @returns {Promise<void>}
 */
export async function clearLLMLog() {
    try {
        const db = await openDB();

        await new Promise((resolve, reject) => {
            try {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                tx.objectStore(STORE_NAME).clear();
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            } catch (err) {
                reject(err);
            }
        });
    } catch (_) {
        // Fire-and-forget
    }
}
